/** The org chart tab's layout and motion decisions, kept out of the components so node-only vitest can pin them. */

import type { SlideWeight } from '../../lib/autoSlide'

/** Fades a card up into place on mount; `motion-safe:` keeps reduced-motion viewers on the static paint. */
export const ENTER_CLASS =
  'motion-safe:transition motion-safe:duration-300 motion-safe:ease-out motion-safe:starting:translate-y-2 motion-safe:starting:opacity-0'

/** Stagger for the `index`-th item of a group, capped so a long group still lands inside the Doherty threshold. */
export function staggerDelay(index: number, stepMs = 40, cap = 8): string {
  return `${Math.min(Math.max(index, 0), cap) * stepMs}ms`
}

/** Three cards over two columns: the third takes the whole second row. */
export function topCardSpanClass(index: number, count: number): string {
  return count === 3 && index === 2 ? '@xl:col-span-2 @3xl:col-span-1' : ''
}

/** The org chart tab's charts grouped by what they describe, each group starting its own page.
 *  Each one-unit chart is paired with another card so no page of three or four units holds a small card alone. */
export const CHART_GROUPS = {
  people: ['byOffice', 'byTeam'],
  workType: ['byWorkType', 'hoursByWorkType'],
  work: ['productCoverage'],
  desks: ['desksByWeekday', 'topDesks'],
} as const

export type OrgChartKey = (typeof CHART_GROUPS)[keyof typeof CHART_GROUPS][number]

/** Every company chart: the org chart tab's, plus the away chart only the dashboard shows. */
export type ChartKey = OrgChartKey | 'away'

export const CHART_ORDER: readonly OrgChartKey[] = [...CHART_GROUPS.people, ...CHART_GROUPS.workType, ...CHART_GROUPS.work, ...CHART_GROUPS.desks]

export function chartGroupOf(key: OrgChartKey): keyof typeof CHART_GROUPS {
  return (Object.keys(CHART_GROUPS) as (keyof typeof CHART_GROUPS)[]).find((g) => (CHART_GROUPS[g] as readonly OrgChartKey[]).includes(key))!
}

export type StatTileKey = 'activeStaff' | 'unapprovedDays' | 'desksInUse' | 'uncoveredProducts' | 'genderRatio' | 'awayToday'

/** ⛔ Leave arrives empty for a viewer without staff, so away today is left out rather than reading as unknown forever. */
export function statTileKeys(staff: boolean): StatTileKey[] {
  const keys: StatTileKey[] = ['activeStaff', 'unapprovedDays', 'desksInUse', 'uncoveredProducts', 'genderRatio']
  return staff ? [...keys, 'awayToday'] : keys
}

/** Six tiles fill two, three or six columns; five use a five-column wide row. */
export function statGridClass(count: number): string {
  return count === 5
    ? 'grid grid-cols-2 gap-2 @lg:grid-cols-3 @5xl:grid-cols-5'
    : 'grid grid-cols-2 gap-2 @lg:grid-cols-3 @5xl:grid-cols-6'
}

/** The fifth of five tiles closes the last row of two or three columns instead of leaving a hole beside it.
 *  Of six, gender and away each take a whole two-column row: half a phone row leaves the gender share bar no width. */
export function statTileSpanClass(index: number, count: number): string {
  if (count === 5 && index === 4) return 'col-span-2 @5xl:col-span-1'
  return count === 6 && index >= 4 ? 'col-span-2 @lg:col-span-1' : ''
}

export type TopCardKey = 'workModes' | 'hoursByProduct' | 'roleMix'

/** ⛔ Work modes today arrive empty for a viewer without staff, so the card is left out rather than reading as nobody logged. */
export function topCardKeys(staff: boolean): TopCardKey[] {
  return staff ? ['workModes', 'hoursByProduct', 'roleMix'] : ['hoursByProduct', 'roleMix']
}

/** Three cards share one row on a wide tab; two take a two-column row so no column is left empty. */
export function topGridClass(count: number): string {
  return count === 3
    ? 'grid grid-cols-1 gap-3 @xl:grid-cols-2 @3xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.4fr)]'
    : 'grid grid-cols-1 gap-3 @xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]'
}

/** The tree scrolls inside its own box: beside the stats it runs to the foot of the screen, stacked above them it stops at 26rem so the figures start on the first screen. */
export function treeHeightClass(filtering: boolean): string {
  return `[--list-chrome:calc(100dvh_-_26rem)] ${filtering ? '@6xl:[--list-chrome:13rem]' : '@6xl:[--list-chrome:9rem]'}`
}

/** How many column units each chart needs to stay legible; the strip packs pages by these. */
export const CHART_WEIGHT: Record<ChartKey, SlideWeight> = {
  byTeam: 2,
  byWorkType: 1,
  byOffice: 1,
  away: 1,
  hoursByWorkType: 1,
  productCoverage: 4,
  desksByWeekday: 2,
  topDesks: 2,
}
